import { useMemo, useState, useEffect } from 'react';
import {
  Button,
  DatePicker,
  Form,
  FormProps,
  Input,
  Select,
  Table,
  Card,
  Row,
  Col,
  Typography,
  notification
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import set from 'lodash/set';

import './tour.scss';
import { useGetAllBranches } from 'hooks/useGetAllBranches';
import { Dayjs } from 'dayjs';
import { toUtcTime } from 'utils';
import { useGetToursData } from 'hooks/useGetToursData';
import { GetToursDataParams } from 'services/api/type/report.type';
import { getColumnToursData } from 'constants/table';
import { TOUR_TYPE_OPTIONS } from 'constants/form';
import { PAGE_SIZE_TOURS_DATA } from 'services/api/module/report.api';
import { getDetailTourURL } from 'routes/constants';
import { ACTION_TOURS } from 'components/tour/action-tour';
import UpdateTourStatusModal from 'components/tour/update-tour-status';
import useModalStore from 'stores/useModal';

export interface TourData {
  key: string;
  tourId: number;
  tourName: string;
  tourStatus: string;
  isSingleTour: boolean;
  totalCustomers: number;
  bookings: any[];
}

export interface BookingData {
  key: string;
  bookingId: number;
  branchName: string;
  travelDate: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
}

function Tour() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const tourType = TOUR_TYPE_OPTIONS(t);
  const { setIsOpenUpdateTourStatus } = useModalStore();

  const { data: branches } = useGetAllBranches();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_TOURS_DATA);
  const [selectedTour, setSelectedTour] = useState<TourData | null>(null);

  const [filterOptions, setFilterOptions] = useState<GetToursDataParams>({});
  const { data, summaryDetails, pagination } = useGetToursData(filterOptions, page, pageSize);

  // Set initial form values
  useEffect(() => {
    form.setFieldsValue({
      type: ''
    });
  }, [form]);

  // Transform data for table
  const transformedData = useMemo(() => {
    return data
      ? Object.entries(data).map(([tourName, tourInfo]: [string, any]) => ({
          key: tourName,
          tourId: tourInfo.tour_id,
          tourName,
          tourStatus: tourInfo.tour_status,
          isSingleTour: tourInfo.is_single_tour,
          totalCustomers: tourInfo.total_customers,
          bookings: tourInfo.bookings || []
        }))
      : [];
  }, [data]);

  const handleClickMenu = (menu: any, record: TourData) => {
    switch (menu.key) {
      case ACTION_TOURS.DETAIL:
        navigate(getDetailTourURL(record.tourId));
        break;
      case ACTION_TOURS.STATUS_ACTION:
        setSelectedTour(record);
        setIsOpenUpdateTourStatus(true);
        break;
      default:
        notification.warning({
          message: t('common.message.actionNotImplemented')
        });
        break;
    }
  };

  // Main Column
  const mainColumns = useMemo(() => {
    return getColumnToursData(t, handleClickMenu);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const handleChangePage = (page: number) => {
    setPage(page);
  };

  const handleTourPackageClick = () => {
    form.setFieldsValue({ type: 'tour_package' });
    if (onFinish) {
      onFinish({ type: 'tour_package' });
    }
  };

  const handleSingleTourClick = () => {
    form.setFieldsValue({ type: 'single_tour' });
    if (onFinish) {
      onFinish({ type: 'single_tour' });
    }
  };

  const onFinish: FormProps['onFinish'] = async (objValue: any) => {
    const truthyValues: any = Object.keys(objValue)
      .filter(key => Boolean(objValue[key]))
      .reduce((cur, next) => {
        return {
          ...cur,
          [next]: objValue[next]
        };
      }, {});

    const { rangeDate, branchIds, tourId, tourName, ...restFilterOptions } = truthyValues;
    if (rangeDate) {
      const startDate: Dayjs = rangeDate[0];
      const endDate: Dayjs = rangeDate[1];

      if (startDate && endDate) {
        set(restFilterOptions, 'start_date', toUtcTime(startDate.add(1, 'day'), 'YYYY-MM-DD'));
        set(restFilterOptions, 'end_date', toUtcTime(endDate.add(1, 'day'), 'YYYY-MM-DD'));
      }
    }

    if (branchIds) {
      set(restFilterOptions, 'branch_ids', branchIds.join(','));
    }

    if (tourId) {
      set(restFilterOptions, 'tour_id', tourId);
    }

    if (tourName) {
      set(restFilterOptions, 'tour_name', tourName);
    }

    if (page !== 1) setPage(1);

    setFilterOptions(restFilterOptions);
  };

  return (
    <div className="pms-reservation-forecast">
      <div className="pms-reservation-forecast__timetable">
        <div className="pms-reservation-forecast__filter">
          <Form form={form} onFinish={onFinish} autoComplete="off">
            <div className="pms-reservation-forecast__filter__left">
              <Form.Item name="tourName">
                <Input
                  placeholder={t('tour.tourName')}
                  style={{ width: '300px', textAlign: 'left' }}
                  className="placeholder-left"
                />
              </Form.Item>
              <Form.Item name="rangeDate">
                <DatePicker.RangePicker
                  className="w-full"
                  placeholder={[t('common.time.fromDate'), t('common.time.toDate')]}
                  format="DD-MM-YYYY"
                />
              </Form.Item>
              <Form.Item name="branchIds">
                <Select
                  mode="multiple"
                  className="w-full"
                  placeholder={t('common.actions.selectBranch')}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={branches?.map(branch => ({
                    key: branch.name,
                    value: branch.id,
                    label: branch.name
                  }))}
                />
              </Form.Item>
              <Form.Item name="type">
                <Select
                  className="w-full"
                  placeholder={t('common.actions.selectTourType')}
                  allowClear
                  options={tourType}
                />
              </Form.Item>
              <Form.Item>
                <Button htmlType="submit" className="ant-btn-secondary btn-submit">
                  {t('common.actions.submitButton')}
                </Button>
              </Form.Item>
            </div>
          </Form>
        </div>

        <div className="pms-reservation-forecast__table">
          {/* Summary Statistics Cards */}
          <Card
            className="info-card"
            title={
              <Typography.Title level={4} className="card-title">
                {t('tour.tourSummary')}
              </Typography.Title>
            }
          >
            <Row gutter={[24, 24]}>
              <Col xs={24} sm={12} md={6}>
                <div className="info-field">
                  <Typography.Text strong className="field-label">
                    {t('tour.totalTours')}
                  </Typography.Text>
                  <div className="field-value">{summaryDetails.total_tours}</div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div className="info-field">
                  <Typography.Text strong className="field-label">
                    {t('tour.totalCustomers')}
                  </Typography.Text>
                  <div className="field-value">{summaryDetails.total_customers}</div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div
                  className="info-field tour-package"
                  onClick={handleTourPackageClick}
                  style={{ cursor: 'pointer' }}
                >
                  <Typography.Text strong className="field-label">
                    {t('tour.totalTourPackage')}
                  </Typography.Text>
                  <div className="field-value">{summaryDetails.total_tour_package}</div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div
                  className="info-field single-tour"
                  onClick={handleSingleTourClick}
                  style={{ cursor: 'pointer' }}
                >
                  <Typography.Text strong className="field-label">
                    {t('tour.totalSingleTour')}
                  </Typography.Text>
                  <div className="field-value">{summaryDetails.total_single_tour}</div>
                </div>
              </Col>
            </Row>
          </Card>

          <Card
            title={
              <Typography.Title level={4} className="card-title">
                {t('tour.tourName')}
              </Typography.Title>
            }
            className="customers-card"
          >
            <Table
              rowKey="key"
              size="small"
              columns={mainColumns}
              dataSource={transformedData}
              pagination={{
                current: page,
                position: ['bottomCenter'],
                total: pagination.total,
                pageSize: pageSize,
                locale: { items_per_page: `/ ${t('common.page')}` },
                showLessItems: true,
                showTotal: total => `${total} ${t('bookingPage.bookingList.item')}`,
                onChange: (page: number) => handleChangePage(page),
                onShowSizeChange: (_, size) => {
                  setPageSize(size);
                }
              }}
              scroll={{ x: 1000, y: 'calc(100vh - 320px)' }}
              bordered
              locale={{
                emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
              }}
            />
          </Card>
        </div>
      </div>
      {selectedTour && <UpdateTourStatusModal tourId={selectedTour.tourId} />}
    </div>
  );
}

export default Tour;
